/**
 * Audio + haptic feedback layer used across games.
 *
 * Web Audio synth (no asset files) keeps the bundle tiny. AudioContext is
 * created lazily on first user gesture (iOS requires this). All emitters
 * no-op if the user has muted via the per-device toggle.
 *
 * Pattern: import { feedback } from '@tt/feedback'; feedback.tick();
 */

const STORAGE_KEY = 'tt:feedback';

interface Prefs { sound: boolean; haptics: boolean }

function loadPrefs(): Prefs {
  if (typeof localStorage === 'undefined') return { sound: true, haptics: true };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { sound: true, haptics: true };
    const p = JSON.parse(raw) as Partial<Prefs>;
    return { sound: p.sound !== false, haptics: p.haptics !== false };
  } catch {
    return { sound: true, haptics: true };
  }
}

function savePrefs(p: Prefs): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

let prefs: Prefs = loadPrefs();
let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const C = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    ctx = new C();
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

function reducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function tone(freq: number, durationMs: number, volume = 0.12, type: OscillatorType = 'sine'): void {
  if (!prefs.sound) return;
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, c.currentTime);
  gain.gain.linearRampToValueAtTime(volume, c.currentTime + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + durationMs / 1000);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + durationMs / 1000 + 0.05);
}

function chord(freqs: number[], durationMs: number, volume = 0.1): void {
  for (const f of freqs) tone(f, durationMs, volume / freqs.length, 'triangle');
}

function vibrate(pattern: number | number[]): void {
  if (!prefs.haptics) return;
  if (typeof navigator === 'undefined') return;
  const n = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
  try { n.vibrate?.(pattern); } catch { /* ignore */ }
}

export const feedback = {
  tick(): void {
    tone(880, 40, 0.06, 'square');
    vibrate(8);
  },
  success(): void {
    chord([523, 659, 784], 220, 0.16);
    vibrate([12, 30, 12]);
  },
  fail(): void {
    tone(196, 220, 0.14, 'sawtooth');
    vibrate(80);
  },
  cheer(): void {
    if (reducedMotion()) {
      tone(659, 200, 0.16);
      return;
    }
    setTimeout(() => tone(523, 80, 0.14, 'triangle'), 0);
    setTimeout(() => tone(659, 80, 0.14, 'triangle'), 90);
    setTimeout(() => tone(784, 80, 0.14, 'triangle'), 180);
    setTimeout(() => tone(1047, 220, 0.18, 'triangle'), 270);
    vibrate([20, 40, 60]);
  },
  bell(): void {
    tone(1047, 110, 0.18, 'triangle');
    setTimeout(() => tone(1568, 60, 0.1, 'triangle'), 60);
    vibrate(15);
  },
  countdownTick(): void {
    tone(660, 70, 0.14, 'square');
    vibrate(6);
  },
  countdownGo(): void {
    chord([523, 784, 1047], 320, 0.18);
    vibrate([20, 30, 40]);
  },
};

export const feedbackPrefs = {
  get(): Prefs { return { ...prefs }; },
  set(next: Partial<Prefs>): void {
    prefs = { ...prefs, ...next };
    savePrefs(prefs);
  },
  toggleSound(): void { feedbackPrefs.set({ sound: !prefs.sound }); },
  toggleHaptics(): void { feedbackPrefs.set({ haptics: !prefs.haptics }); },
};
